/**
 * Profitual API client for the Canva App.
 * BACKEND_HOST is injected by webpack DefinePlugin.
 */

declare const BACKEND_HOST: string;

const API_BASE = `${BACKEND_HOST}/api/canva`;

export interface LinkedOrg {
  orgId: string;
  orgName: string;
}

interface ExchangeResponse {
  status: "linked" | "unlinked";
  orgs: LinkedOrg[];
}

interface SelectOrgResponse {
  token: string;
  expiresAt: string;
  orgId: string;
  orgName: string;
}

interface LinkResponse {
  status: "linked";
  token: string;
  expiresAt: string;
  orgId: string;
  orgName: string;
}

export interface Scenario {
  id: string;
  name: string;
}

export interface FinancialModel {
  id: string;
  name: string;
  scenarios: Scenario[];
}

export interface MetricValue {
  value: number;
  previousValue: number;
  change: number | null; // MoM % change, null if previous was 0
  formatted: string;
  label: string;
}

export interface MetricsResponse {
  period: string;
  metrics: Record<string, MetricValue>;
}

export interface ChartResponse {
  dataUrl: string;
  width: number;
  height: number;
}

export interface DataTableRow {
  month: string; // YYYY-MM-01
  revenue: number;
  expenses: number;
  netIncome: number;
  mrr: number;
  arr: number;
  cashBalance: number;
  monthlyBurn: number;
  headcount: number;
}

export interface DataTableResponse {
  period: { start: string; end: string; months: number };
  rows: DataTableRow[];
}

export interface BreakdownRow {
  name: string;
  value: number;
  percentOfTotal: number;
}

export interface BreakdownResponse {
  type: "revenue" | "expense" | "headcount";
  month: string;
  total: number;
  rows: BreakdownRow[];
}

export interface IncomeStatementRow {
  month: string;
  revenue: number;
  cogs: number;
  grossProfit: number;
  operatingExpenses: number;
  netIncome: number;
}

export interface CashFlowRow {
  month: string;
  beginningCash: number;
  operating: number;
  investing: number;
  financing: number;
  netChange: number;
  endingCash: number;
}

export interface StatementResponse {
  type: "income" | "cashflow";
  period: { start: string; end: string; months: number };
  rows: IncomeStatementRow[] | CashFlowRow[];
}

class ProfitualClient {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
  }

  clearToken() {
    this.token = null;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(
        (body as { error?: string }).error ?? `Request failed: ${res.status}`
      );
    }

    return res.json() as Promise<T>;
  }

  /** Verify Canva JWT and return the full list of Profitual orgs linked to this Canva user. */
  async exchange(canvaToken: string): Promise<ExchangeResponse> {
    return this.request<ExchangeResponse>("/auth/exchange", {
      method: "POST",
      body: JSON.stringify({ canvaToken }),
    });
  }

  /** Obtain a Profitual session token scoped to a specific linked org. */
  async selectOrg(canvaToken: string, orgId: string): Promise<SelectOrgResponse> {
    return this.request<SelectOrgResponse>("/auth/select-org", {
      method: "POST",
      body: JSON.stringify({ canvaToken, orgId }),
    });
  }

  /** Link a Canva user to a Profitual account after Clerk login. */
  async link(canvaToken: string, clerkSessionToken: string): Promise<LinkResponse> {
    return this.request<LinkResponse>("/auth/link", {
      method: "POST",
      body: JSON.stringify({ canvaToken, clerkSessionToken }),
    });
  }

  /** Disconnect the linked Profitual account. */
  async disconnect(): Promise<void> {
    await this.request("/auth/disconnect", { method: "POST" });
    this.clearToken();
  }

  /** List financial models and scenarios for the linked org. */
  async getScenarios(): Promise<{ models: FinancialModel[] }> {
    return this.request("/scenarios");
  }

  /** Get key metrics for a scenario at a given month. */
  async getMetrics(scenarioId: string, month: string): Promise<MetricsResponse> {
    const params = new URLSearchParams({ scenarioId, month });
    return this.request(`/metrics?${params}`);
  }

  /**
   * Get variance data (forecast vs actuals, or period-over-period) for a scenario.
   *
   * Query params match `VarianceSectionConfig` on the platform:
   * - `comparisonType`: "forecast_vs_actuals" | "actuals_vs_previous_period"
   * - `thresholdPercent`: number — highlight band; drives `isOverThreshold` on rows
   */
  async getVariance(
    scenarioId: string,
    periodStart: string,
    periodEnd: string,
    options?: {
      comparisonType?: "forecast_vs_actuals" | "actuals_vs_previous_period";
      thresholdPercent?: number;
    }
  ): Promise<VarianceResponse> {
    const params = new URLSearchParams({ scenarioId, periodStart, periodEnd });
    if (options?.comparisonType) params.set("comparisonType", options.comparisonType);
    if (options?.thresholdPercent != null) {
      params.set("thresholdPercent", String(options.thresholdPercent));
    }
    return this.request(`/variance?${params}`);
  }

  /** Monthly time series for the Data Connector intent (feeds Canva charts/tables). */
  async getDataTable(
    scenarioId: string,
    months: number = 12,
    endMonth?: string
  ): Promise<DataTableResponse> {
    const params = new URLSearchParams({ scenarioId, months: String(months) });
    if (endMonth) params.set("endMonth", endMonth);
    return this.request(`/datatable?${params}`);
  }

  /** Categorical breakdown for a single month — revenue lines, expense lines, or headcount by department. */
  async getBreakdown(
    scenarioId: string,
    type: "revenue" | "expense" | "headcount",
    month: string
  ): Promise<BreakdownResponse> {
    const params = new URLSearchParams({ scenarioId, type, month });
    return this.request(`/datatable/breakdown?${params}`);
  }

  /** Monthly income-statement or cash-flow time series. */
  async getStatement(
    scenarioId: string,
    type: "income" | "cashflow",
    months: number = 12,
    endMonth?: string
  ): Promise<StatementResponse> {
    const params = new URLSearchParams({
      scenarioId,
      type,
      months: String(months),
    });
    if (endMonth) params.set("endMonth", endMonth);
    return this.request(`/datatable/statement?${params}`);
  }

  /** Render a chart as a PNG and return as base64 data URL. */
  async getChart(
    scenarioId: string,
    chartType: string,
    periods: number,
    endMonth: string
  ): Promise<ChartResponse> {
    return this.request("/chart", {
      method: "POST",
      body: JSON.stringify({ scenarioId, chartType, periods, endMonth }),
    });
  }
}

export interface VarianceRow {
  lineName: string;
  tableName?: string;
  forecast: string;
  actual: string;
  variance: string;
  variancePercent: number;
  isFavorable: boolean;
  isOverThreshold: boolean;
}

export interface VarianceSectionTotal {
  tableName: string;
  forecast: string;
  actual: string;
  variance: string;
  variancePercent: number;
  isFavorable: boolean;
}

/** Profitability subtotal pulled from the Deductions table (Earnings / Profit (Loss)). */
export interface VarianceComputedLine {
  name: string;
  forecast: string;
  actual: string;
  variance: string;
  variancePercent: number;
  isFavorable: boolean;
}

export interface VarianceResponse {
  rows: VarianceRow[];
  summary: {
    totalForecast: string;
    totalActual: string;
    totalVariance: string;
  };
  /** Per-tableName totals; rendered as bold "Total X" rows after each section. */
  sectionTotals?: VarianceSectionTotal[];
  /** Profitability subtotals (Earnings / Profit (Loss)); render after sections. */
  computedLines?: VarianceComputedLine[];
  message?: string;
}

export const api = new ProfitualClient();
